"use client";

import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Bell,
  Check,
  ChevronRight,
  Download,
  Info,
  Wallet,
  X,
} from "lucide-react";
import { UsageChart } from "./usage-chart";
import { SelectFilter } from "./filters";
import {
  TIMEZONES,
  formatCNY,
  formatTokens,
  modelNames,
  shiftLabel,
  sliceByRange,
  sumUsage,
  timezoneById,
  TIME_RANGES,
  wallet,
  type DailyUsage,
  type TimeRange,
} from "@/lib/usage-data";

type Tab = "model" | "key";

/** 与后端 /api/usage 返回的结构对应 */
interface UsageKeyRow {
  keyId: number;
  keyName: string;
  keySensitiveId: string;
  isDemo: boolean;
  created: string;
  lastUse: string | null;
  daily: DailyUsage[];
}

/** 将多个 Key 的 30 天日数据逐天求和（"全部" = 各 Key 之和，总量与快照一致） */
function mergeDaily(rows: UsageKeyRow[]): DailyUsage[] {
  if (rows.length === 0) return [];
  const n = rows[0].daily.length;
  return Array.from({ length: n }, (_, i) => ({
    day: i,
    label: rows[0].daily[i].label,
    requests: rows.reduce((s, k) => s + k.daily[i].requests, 0),
    tokens: rows.reduce((s, k) => s + k.daily[i].tokens, 0),
    cost: rows.reduce((s, k) => s + k.daily[i].cost, 0),
  }));
}

function StatCard({
  label,
  value,
  unit,
}: {
  label: string;
  value: string;
  unit?: string;
}) {
  return (
    <section className="rounded-xl border border-[#e8eaed] bg-white p-5">
      <div className="flex items-start justify-between">
        <h2 className="text-sm text-[#545557]">{label}</h2>
        <Info className="h-4 w-4 text-[#c3c6cb]" />
      </div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <span className="text-2xl font-semibold tracking-tight text-[#212327] tabular-nums">{value}</span>
        {unit && <span className="text-xs text-[#a2a4a6]">{unit}</span>}
      </div>
    </section>
  );
}

/** 图表 y 轴刻度压缩显示：如 15000 → 1.5万、1.2e9 → 12亿 */
function fmtAxis(v: number): string {
  if (v === 0) return "0";
  const abs = Math.abs(v);
  if (abs >= 1e8) return `${(v / 1e8).toFixed(abs / 1e8 < 10 ? 1 : 0)}亿`;
  if (abs >= 1e4) return `${(v / 1e4).toFixed(abs / 1e4 < 10 ? 1 : 0)}万`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(abs / 1e3 < 10 ? 1 : 0)}k`;
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="flex h-[140px] items-center justify-center rounded-lg border border-dashed border-[#e0e2e5] bg-[#fafbfc] text-sm text-[#a2a4a6]">
      {text}
    </div>
  );
}

type SortKey = "date" | "requests" | "tokens" | "cost";
type SortDir = "asc" | "desc";

/** 每日用量明细表：可排序 + 每页 10 行分页 */
function DailyDetailTable({ rows }: { rows: DailyUsage[] }) {
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const sorted = useMemo(() => {
    const arr = [...rows];
    if (sortKey !== "date") {
      const numKey = sortKey as Exclude<SortKey, "date">;
      arr.sort((a, b) => (sortDir === "asc" ? a[numKey] - b[numKey] : b[numKey] - a[numKey]));
    } else if (sortDir === "desc") {
      arr.reverse();
    }
    return arr;
  }, [rows, sortKey, sortDir]);

  // 筛选条件或排序变化时回到第一页
  useEffect(() => setPage(1), [rows, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageRows = sorted.slice((page - 1) * pageSize, page * pageSize);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      setSortDir(k === "date" ? "asc" : "desc");
    }
  };

  const headCell = (label: string, k: SortKey, align: "left" | "right" = "right") => (
    <th className={`px-5 py-3 ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className={`inline-flex items-center gap-1 text-xs font-medium transition-colors ${
          sortKey === k ? "text-[#4d6bfe]" : "text-[#7f8287] hover:text-[#212327]"
        }`}
      >
        {label}
        {sortKey === k ? (
          sortDir === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-50" />
        )}
      </button>
    </th>
  );

  if (rows.length === 0) {
    return (
      <div className="p-5">
        <EmptyState text="当前筛选范围内暂无明细数据" />
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#f0f1f3] bg-[#fafbfc]">
              {headCell("日期", "date", "left")}
              {headCell("API 请求次数", "requests")}
              {headCell("Tokens", "tokens")}
              {headCell("消费金额（CNY）", "cost")}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr
                key={`${r.day}-${r.label}`}
                className="border-b border-[#f5f6f8] transition-colors hover:bg-[#fafbfc]"
              >
                <td className="px-5 py-3 text-left text-[#212327]">{r.label}</td>
                <td className="px-5 py-3 text-right tabular-nums text-[#212327]">{r.requests}</td>
                <td className="px-5 py-3 text-right tabular-nums text-[#212327]">
                  {formatTokens(r.tokens)}
                </td>
                <td className="px-5 py-3 text-right tabular-nums text-[#212327]">
                  {formatCNY(r.cost)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-5 py-3">
        <p className="text-xs text-[#7f8287]">共 {sorted.length} 条记录</p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded-lg border border-[#e5e5e5] bg-white px-3 py-1.5 text-xs text-[#545557] transition-colors hover:border-[#b8bdc4] disabled:cursor-not-allowed disabled:opacity-40"
          >
            上一页
          </button>
          <span className="text-xs tabular-nums text-[#7f8287]">
            {page} / {pageCount}
          </span>
          <button
            type="button"
            disabled={page >= pageCount}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-lg border border-[#e5e5e5] bg-white px-3 py-1.5 text-xs text-[#545557] transition-colors hover:border-[#b8bdc4] disabled:cursor-not-allowed disabled:opacity-40"
          >
            下一页
          </button>
        </div>
      </div>
    </div>
  );
}

function Modal({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#212327]/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#212327]">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[#7f8287] hover:bg-[#f5f5f5]"
            aria-label="关闭"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {subtitle && <p className="mt-1 text-xs text-[#7f8287]">{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
        checked ? "bg-[#4d6bfe]" : "bg-[#d4d6da]"
      }`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
          checked ? "left-[22px]" : "left-0.5"
        }`}
      />
    </button>
  );
}

export function UsagePage() {
  const [bannerClosed, setBannerClosed] = useState(false);
  const [priceNoticeClosed, setPriceNoticeClosed] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange>("近 30 天");
  const [apiKeyFilter, setApiKeyFilter] = useState("全部");
  const [activeTab, setActiveTab] = useState<Tab>("model");
  const [balance, setBalance] = useState(wallet.balance);
  const [totalCost, setTotalCost] = useState(wallet.totalCost);
  const [keyOptions, setKeyOptions] = useState<string[]>(["全部"]);
  const [keyUsage, setKeyUsage] = useState<UsageKeyRow[]>([]);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState(50);
  const [toast, setToast] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set(["deepseek-v4-flash"]));
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [alertThreshold, setAlertThreshold] = useState(20);
  const [tzId, setTzId] = useState("Asia/Shanghai");
  const [alertOpen, setAlertOpen] = useState(false);
  const [tzOpen, setTzOpen] = useState(false);
  const [alertDraftEnabled, setAlertDraftEnabled] = useState(false);
  const [alertDraftThreshold, setAlertDraftThreshold] = useState(20);
  const [tzDraftId, setTzDraftId] = useState("Asia/Shanghai");
  const [savingAlert, setSavingAlert] = useState(false);
  const [savingTz, setSavingTz] = useState(false);
  const [exportDim, setExportDim] = useState<"daily" | "model" | "key">("daily");

  const timezone = timezoneById(tzId);
  const allDaily = useMemo(() => {
    const selected =
      apiKeyFilter === "全部" ? keyUsage : keyUsage.filter((k) => k.keyName === apiKeyFilter);
    return mergeDaily(selected);
  }, [keyUsage, apiKeyFilter]);
  const rows = useMemo(
    () => sliceByRange(timeRange, allDaily).map((r) => ({ ...r, label: shiftLabel(r.label, timezone.offset) })),
    [timeRange, allDaily, timezone.offset]
  );
  const totals = useMemo(() => sumUsage(rows), [rows]);
  // 该账号所有调用均来自 deepseek-v4-flash，故范围汇总只归属它，其余模型为 0
  const perModel = useMemo(() => {
    const map: Record<string, { requests: number; tokens: number; cost: number }> = {};
    for (const name of modelNames) {
      map[name] =
        name === "deepseek-v4-flash"
          ? totals
          : { requests: 0, tokens: 0, cost: 0 };
    }
    return map;
  }, [totals]);
  const costSeries = useMemo(() => rows.map((d) => ({ label: d.label, value: d.cost })), [rows]);
  const reqSeries = useMemo(() => rows.map((d) => ({ label: d.label, value: d.requests })), [rows]);
  const tokSeries = useMemo(() => rows.map((d) => ({ label: d.label, value: d.tokens })), [rows]);
  const hasData = totals.requests > 0;

  // 挂载后拉取当前用户真实余额与 API Keys（用于筛选器）
  useEffect(() => {
    fetch("/api/wallet")
      .then((r) => r.json())
      .then((d) => {
        if (d.ok) {
          setBalance(d.wallet.balance);
          setTotalCost(d.wallet.totalCost);
        }
      })
      .catch(() => {});
    fetch("/api/usage")
      .then((r) => r.json())
      .then((d) => {
        if (d.ok) {
          setKeyUsage(d.usage);
          setKeyOptions(["全部", ...d.usage.map((k: { keyName: string }) => k.keyName)]);
        }
      })
      .catch(() => {});
    fetch("/api/preferences")
      .then((r) => r.json())
      .then((d) => {
        if (d.ok) {
          const p = d.preferences;
          setAlertEnabled(p.alertEnabled);
          setAlertThreshold(p.alertThreshold);
          setTzId(p.timezone);
          setAlertDraftEnabled(p.alertEnabled);
          setAlertDraftThreshold(p.alertThreshold);
          setTzDraftId(p.timezone);
        }
      })
      .catch(() => {});
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3200);
  };

  const toggleModel = (name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const exportCsv = () => {
    const esc = (c: string | number) => {
      const s = String(c);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    let header: string[];
    let lines: (string | number)[][];
    let dimLabel: string;
    if (exportDim === "model") {
      header = ["模型", "API 请求次数", "Tokens", "消费金额(CNY)"];
      dimLabel = "按模型汇总";
      lines = modelNames.map((name) => [
        name,
        perModel[name].requests,
        perModel[name].tokens,
        perModel[name].cost.toFixed(6),
      ]);
    } else if (exportDim === "key") {
      header = ["API Key", "Key ID", "API 请求次数", "Tokens", "消费金额(CNY)"];
      dimLabel = "按 Key 汇总";
      lines = keyUsage.map((k) => {
        const s = sumUsage(sliceByRange(timeRange, k.daily));
        return [k.keyName, k.keySensitiveId, s.requests, s.tokens, s.cost.toFixed(6)];
      });
    } else {
      header = ["日期", "API 请求次数", "Tokens", "消费金额(CNY)"];
      dimLabel = "日期明细";
      lines = rows.map((d) => [d.label, d.requests, d.tokens, d.cost.toFixed(6)]);
    }
    const csv = [header.map(esc).join(","), ...lines.map((l) => l.map(esc).join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `deepseek-usage-${exportDim}-${timeRange.replace(/ /g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`已导出${dimLabel}（${lines.length} 行 · ${timeRange}）`);
  };

  const confirmRecharge = async () => {
    try {
      const res = await fetch("/api/recharge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: rechargeAmount, method: "微信支付" }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        showToast(data.error ?? "充值失败");
        return;
      }
      setBalance(data.balance);
      setRechargeOpen(false);
      showToast(`充值成功：+¥${rechargeAmount.toFixed(2)}`);
    } catch {
      showToast("网络异常，请稍后重试");
    }
  };

  const saveAlert = async () => {
    setSavingAlert(true);
    try {
      const res = await fetch("/api/preferences", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ alertEnabled: alertDraftEnabled, alertThreshold: alertDraftThreshold }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        showToast(data.error ?? "保存失败");
        return;
      }
      setAlertEnabled(data.preferences.alertEnabled);
      setAlertThreshold(data.preferences.alertThreshold);
      setAlertOpen(false);
      showToast(
        data.preferences.alertEnabled
          ? `余额预警已开启：余额低于 ¥${data.preferences.alertThreshold} 时提醒`
          : "余额预警已关闭"
      );
    } catch {
      showToast("网络异常，请稍后重试");
    } finally {
      setSavingAlert(false);
    }
  };

  const saveTimezone = async () => {
    setSavingTz(true);
    try {
      const res = await fetch("/api/preferences", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ timezone: tzDraftId }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        showToast(data.error ?? "保存失败");
        return;
      }
      setTzId(data.preferences.timezone);
      setTzOpen(false);
      showToast(`已切换为 ${timezoneById(data.preferences.timezone).label}`);
    } catch {
      showToast("网络异常，请稍后重试");
    } finally {
      setSavingTz(false);
    }
  };

  return (
    <main className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-8 md:py-8">
      {/* toast */}
      {toast && (
        <div className="fixed right-4 top-4 z-50 flex items-center gap-2 rounded-lg bg-[#212327] px-4 py-2.5 text-sm text-white shadow-lg">
          <Check className="h-4 w-4 text-[#22c55e]" />
          {toast}
        </div>
      )}

      {/* announcement banner */}
      {!bannerClosed && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-[#e0e7ff] bg-[#eef2ff] px-4 py-3 text-sm text-[#4d6bfe]">
          <span className="flex-1">
            多模态视觉理解模型 <span className="font-semibold">DeepSeek-V4-Flash-Vision-Exp</span> 已上线
            DeepSeek API 平台，支持 Chat Completions、Messages、Responses 三种格式调用，欢迎测试和反馈。{" "}
            <a href="#" onClick={(e) => e.preventDefault()} className="font-medium underline underline-offset-2">
              点击查看详情
            </a>
          </span>
          <button
            type="button"
            onClick={() => setBannerClosed(true)}
            className="rounded p-1 text-[#4d6bfe]/70 hover:bg-[#4d6bfe]/10 hover:text-[#4d6bfe]"
            aria-label="关闭公告"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[#212327]">用量信息</h1>
          <p className="mt-1 text-xs text-[#7f8287]">
            所有日期均按 <span className="font-medium text-[#545557]">{timezone.label}</span> 时间显示，数据可能有 5
            分钟延迟。
          </p>
        </div>
      </div>

      {/* price notice */}
      {!priceNoticeClosed && (
        <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-[#fce8e6] bg-[#fef4f2] px-4 py-3 text-sm text-[#8a4a3f]">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p className="flex-1">
            我们将于北京时间2026年8月23日（周日）00:00起，对峰谷计费规则做出调整，北京时间周末（周六、周日）全天不再区分峰谷时段，统一按照低谷时段价格收取调用费用。请查询{" "}
            <a href="#" onClick={(e) => e.preventDefault()} className="font-medium underline underline-offset-2">
              价格页面
            </a>{" "}
            详情，合理安排您的使用。
          </p>
          <button
            type="button"
            onClick={() => setPriceNoticeClosed(true)}
            className="shrink-0 rounded-md border border-[#e8c9c3] px-3 py-1 text-xs font-medium text-[#8a4a3f] hover:bg-[#fde8e5]"
          >
            知道了
          </button>
        </div>
      )}

      {/* balance card */}
      <section className="mt-5 overflow-hidden rounded-xl border border-[#e8eaed] bg-white">
        <div className="flex flex-col divide-y divide-[#f0f1f3] sm:flex-row sm:divide-x sm:divide-y-0">
          <div className="flex-1 p-6">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-1.5 text-sm text-[#545557]">
                <Wallet className="h-4 w-4 text-[#4d6bfe]" />
                充值余额
              </h2>
              <span className="flex items-center gap-1 text-xs text-[#7f8287]">
                {alertEnabled ? (
                  <span className="font-medium text-[#d33a2c]">余额低于 ¥{alertThreshold} 时提醒</span>
                ) : (
                  "余额预警未开启"
                )}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setAlertOpen(true);
                  }}
                  className="font-medium text-[#4d6bfe] hover:underline"
                >
                  去设置
                </a>
              </span>
            </div>
            <div className="mt-3 flex items-baseline gap-1.5">
              <span
                className={`text-3xl font-semibold tracking-tight tabular-nums ${
                  alertEnabled && balance <= alertThreshold ? "text-[#d33a2c]" : "text-[#212327]"
                }`}
              >
                {formatCNY(balance)}
              </span>
              <span className="text-sm text-[#a2a4a6]">{wallet.currency}</span>
            </div>
            {alertEnabled && balance <= alertThreshold && (
              <p className="mt-2 text-xs text-[#d33a2c]">余额已低于预警阈值 ¥{alertThreshold}，请及时充值</p>
            )}
            <button
              type="button"
              onClick={() => setRechargeOpen(true)}
              className="mt-4 inline-flex h-9 items-center justify-center rounded-lg bg-[#4d6bfe] px-4 text-sm font-medium text-white transition-colors hover:bg-[#3d57e0]"
            >
              去充值
            </button>
          </div>

          <div className="flex-1 p-6">
            <h2 className="text-sm text-[#545557]">累计消费金额</h2>
            <div className="mt-3 flex items-baseline gap-1.5">
              <span className="text-3xl font-semibold tracking-tight text-[#212327] tabular-nums">
                {formatCNY(totalCost)}
              </span>
              <span className="text-sm text-[#a2a4a6]">{wallet.currency}</span>
            </div>
            <p className="mt-2 text-xs text-[#a2a4a6]">
              当前筛选范围：{timeRange}
              {apiKeyFilter !== "全部" && ` · ${apiKeyFilter}`}，消费 {formatCNY(totals.cost)}
            </p>
          </div>
        </div>
      </section>

      {/* filters */}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <SelectFilter
          label="时间维度"
          value={timeRange}
          onChange={(v) => setTimeRange(v as TimeRange)}
          options={TIME_RANGES}
        />
        <SelectFilter
          label="API Key"
          value={apiKeyFilter}
          onChange={setApiKeyFilter}
          options={keyOptions}
        />
        <div className="ml-auto flex items-center gap-3">
          <SelectFilter
            label="导出维度"
            value={exportDim === "model" ? "按模型汇总" : exportDim === "key" ? "按 Key 汇总" : "日期明细"}
            onChange={(v) => setExportDim(v === "按模型汇总" ? "model" : v === "按 Key 汇总" ? "key" : "daily")}
            options={["日期明细", "按模型汇总", "按 Key 汇总"]}
          />
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[#e5e5e5] bg-white px-3.5 text-sm text-[#212327] transition-colors hover:border-[#b8bdc4]"
          >
            <Download className="h-4 w-4 text-[#545557]" />
            导出
          </button>
          <button
            type="button"
            onClick={() => {
              setTzDraftId(tzId);
              setTzOpen(true);
            }}
            className="inline-flex h-9 items-center gap-1 rounded-lg border border-[#e5e5e5] bg-white px-3.5 text-sm text-[#212327] transition-colors hover:border-[#b8bdc4]"
          >
            <Bell className="h-4 w-4 text-[#545557]" />
            选择时区
          </button>
        </div>
      </div>

      {/* stat cards */}
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="消费金额" value={formatCNY(totals.cost)} unit="CNY" />
        <StatCard label="API 请求次数" value={String(totals.requests)} unit="次" />
        <StatCard label="Tokens" value={formatTokens(totals.tokens)} />
      </div>

      {/* consumption chart */}
      <section className="mt-5 rounded-xl border border-[#e8eaed] bg-white">
        <div className="flex items-center gap-1 border-b border-[#f0f1f3] px-5 pt-4">
          {(["model", "key"] as Tab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`rounded-lg px-4 py-2 text-sm transition-colors ${
                activeTab === tab
                  ? "bg-[#f0f2ff] font-medium text-[#4d6bfe]"
                  : "text-[#7f8287] hover:text-[#212327]"
              }`}
            >
              {tab === "model" ? "模型" : "API Key"}
            </button>
          ))}
        </div>

        <div className="p-5">
          <div className="mb-1 flex items-baseline justify-between">
            <h3 className="text-sm font-medium text-[#212327]">
              消费金额（CNY）
              {activeTab === "key" && apiKeyFilter !== "全部" && ` · ${apiKeyFilter}`}
            </h3>
            <span className="text-sm font-semibold text-[#212327] tabular-nums">
              {formatCNY(totals.cost)}
            </span>
          </div>
          {hasData ? (
            <UsageChart
              data={costSeries}
              formatY={fmtAxis}
              formatTip={(p) => `${p.label} · ¥${p.value.toFixed(2)}`}
              xLabelEvery={Math.max(1, Math.round(costSeries.length / 3))}
            />
          ) : (
            <EmptyState text={`${apiKeyFilter !== "全部" ? `API Key「${apiKeyFilter}」` : "该时间范围"}内暂无调用记录`} />
          )}
        </div>
      </section>

      {/* daily detail table */}
      <section className="mt-5 rounded-xl border border-[#e8eaed] bg-white">
        <div className="border-b border-[#f0f1f3] px-5 py-4">
          <h3 className="text-sm font-medium text-[#212327]">每日用量明细</h3>
          <p className="mt-0.5 text-xs text-[#7f8287]">
            按 {timezone.label} 显示 · 每页 10 条 · 点击列头可排序
          </p>
        </div>
        <DailyDetailTable rows={rows} />
      </section>

      {/* detail: models or api keys */}
      <section className="mt-5 space-y-3">
        {activeTab === "model" ? (
          modelNames.map((name) => {
            const s = perModel[name];
            const isOpen = expanded.has(name);
            const hasModelData = s.requests > 0;
            return (
              <div key={name} className="overflow-hidden rounded-xl border border-[#e8eaed] bg-white">
                <button
                  type="button"
                  onClick={() => toggleModel(name)}
                  className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-[#fafbfc]"
                >
                  <ChevronRight
                    className={`h-4 w-4 shrink-0 text-[#a2a4a6] transition-transform ${isOpen ? "rotate-90" : ""}`}
                  />
                  <span className="flex-1 truncate font-mono text-sm text-[#212327]">{name}</span>
                  {hasModelData ? (
                    <>
                      <span className="hidden text-sm text-[#545557] tabular-nums sm:block">
                        {s.requests} 次请求
                      </span>
                      <span className="hidden text-sm text-[#545557] tabular-nums sm:block">
                        {formatTokens(s.tokens)} Tokens
                      </span>
                    </>
                  ) : (
                    <span className="text-xs text-[#a2a4a6]">无调用</span>
                  )}
                  <span className="text-sm font-semibold text-[#212327] tabular-nums">
                    {formatCNY(s.cost)}
                  </span>
                </button>

                {isOpen && (
                  <div className="grid grid-cols-1 gap-4 border-t border-[#f0f1f3] p-5 lg:grid-cols-2">
                    {hasModelData ? (
                      <>
                        <div>
                          <div className="mb-1 flex items-baseline justify-between">
                            <h3 className="text-sm font-medium text-[#212327]">API 请求次数</h3>
                            <span className="text-sm font-semibold text-[#212327] tabular-nums">{s.requests}</span>
                          </div>
                          <UsageChart
                            data={reqSeries}
                            color="#22c55e"
                            height={150}
                            formatY={fmtAxis}
                            formatTip={(p) => `${p.label} · ${p.value} 次`}
                            xLabelEvery={Math.max(1, Math.round(reqSeries.length / 3))}
                          />
                        </div>
                        <div>
                          <div className="mb-1 flex items-baseline justify-between">
                            <h3 className="text-sm font-medium text-[#212327]">Tokens</h3>
                            <span className="text-sm font-semibold text-[#212327] tabular-nums">
                              {formatTokens(s.tokens)}
                            </span>
                          </div>
                          <UsageChart
                            data={tokSeries}
                            color="#4d6bfe"
                            height={150}
                            formatY={fmtAxis}
                            formatTip={(p) => `${p.label} · ${formatTokens(p.value)}`}
                            xLabelEvery={Math.max(1, Math.round(tokSeries.length / 3))}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="lg:col-span-2">
                        <EmptyState text={`${name} 在所选时间范围内无调用记录`} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        ) : keyUsage.length === 0 ? (
          <EmptyState text="正在加载 API Key 用量…" />
        ) : (
          keyUsage.map((key) => {
            const keyDaily = sliceByRange(timeRange, key.daily);
            const keyTotals = sumUsage(keyDaily);
            const keyReqSeries = keyDaily.map((d) => ({
              label: shiftLabel(d.label, timezone.offset),
              value: d.requests,
            }));
            const keyHasData = keyTotals.requests > 0;
            return (
              <div key={key.keyId} className="overflow-hidden rounded-xl border border-[#e8eaed] bg-white">
                <div className="flex w-full items-center gap-3 px-5 py-4">
                  <span className="flex-1 truncate text-sm text-[#212327]">
                    {key.keyName}{" "}
                    <span className="font-mono text-xs text-[#a2a4a6]">({key.keySensitiveId})</span>
                    {key.isDemo && (
                      <span className="ml-1 rounded bg-[#eef2ff] px-1 py-0.5 text-[10px] font-medium text-[#4d6bfe]">
                        演示
                      </span>
                    )}
                  </span>
                  <span className="text-sm text-[#545557] tabular-nums">{keyTotals.requests} 次请求</span>
                  <span className="text-sm font-semibold text-[#212327] tabular-nums">
                    {formatCNY(keyTotals.cost)}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-4 border-t border-[#f0f1f3] p-5">
                  <div>
                    <div className="mb-1 flex items-baseline justify-between">
                      <h3 className="text-sm font-medium text-[#212327]">API 请求次数</h3>
                      <span className="text-sm font-semibold text-[#212327] tabular-nums">{keyTotals.requests}</span>
                    </div>
                    {keyHasData ? (
                      <UsageChart
                        data={keyReqSeries}
                        color="#22c55e"
                        formatY={fmtAxis}
                        formatTip={(p) => `${p.label} · ${p.value} 次`}
                        xLabelEvery={Math.max(1, Math.round(keyReqSeries.length / 3))}
                      />
                    ) : (
                      <EmptyState text={`API Key「${key.keyName}」在所选时间范围内暂无调用记录`} />
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </section>

      <p className="mt-6 text-center text-xs text-[#a2a4a6]">
        本页面为 DeepSeek 开放平台「用量信息」的克隆演示：内置演示 Key 为真实接口快照，本地 API Key 调用记录来自本地演示数据
      </p>

      {/* recharge dialog */}
      {rechargeOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-[#212327]/40 p-4"
          onClick={() => setRechargeOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="充值"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#212327]">充值</h3>
              <button
                type="button"
                onClick={() => setRechargeOpen(false)}
                className="rounded p-1 text-[#7f8287] hover:bg-[#f5f5f5]"
                aria-label="关闭"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1 text-xs text-[#7f8287]">选择充值金额（CNY）</p>

            <div className="mt-4 grid grid-cols-4 gap-2">
              {[10, 50, 100, 200].map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => setRechargeAmount(amt)}
                  className={`rounded-lg border px-2 py-3 text-sm font-medium tabular-nums transition-colors ${
                    rechargeAmount === amt
                      ? "border-[#4d6bfe] bg-[#f0f2ff] text-[#4d6bfe]"
                      : "border-[#e5e5e5] text-[#545557] hover:border-[#b8bdc4]"
                  }`}
                >
                  ¥{amt}
                </button>
              ))}
            </div>

            <div className="mt-4 flex items-baseline justify-between rounded-lg bg-[#fafbfc] px-4 py-3">
              <span className="text-sm text-[#545557]">到账余额</span>
              <span className="text-lg font-semibold text-[#212327] tabular-nums">
                ¥{rechargeAmount.toFixed(2)}
              </span>
            </div>

            <button
              type="button"
              onClick={confirmRecharge}
              className="mt-4 h-10 w-full rounded-lg bg-[#4d6bfe] text-sm font-medium text-white transition-colors hover:bg-[#3d57e0]"
            >
              立即充值
            </button>
            <p className="mt-3 text-center text-xs text-[#a2a4a6]">
              演示环境：支付为本地模拟，充值后余额持久化到本地数据库
            </p>
          </div>
        </div>
      )}

      {/* balance alert dialog */}
      {alertOpen && (
        <Modal
          title="余额预警"
          subtitle="开启后，当充值余额低于设定阈值时，平台将提醒你及时充值。"
          onClose={() => setAlertOpen(false)}
        >
          <div className="mt-4 flex items-center justify-between rounded-lg border border-[#eef0f3] bg-[#fafbfc] px-4 py-3">
            <span className="text-sm text-[#212327]">开启余额预警</span>
            <Toggle checked={alertDraftEnabled} onChange={setAlertDraftEnabled} />
          </div>

          {alertDraftEnabled && (
            <div className="mt-4">
              <p className="text-sm text-[#545557]">预警阈值（余额低于该金额时提醒）</p>
              <div className="mt-2 grid grid-cols-5 gap-2">
                {[5, 10, 20, 50, 100].map((amt) => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setAlertDraftThreshold(amt)}
                    className={`rounded-lg border px-2 py-2 text-sm font-medium tabular-nums transition-colors ${
                      alertDraftThreshold === amt
                        ? "border-[#4d6bfe] bg-[#f0f2ff] text-[#4d6bfe]"
                        : "border-[#e5e5e5] text-[#545557] hover:border-[#b8bdc4]"
                    }`}
                  >
                    ¥{amt}
                  </button>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-sm text-[#545557]">自定义</span>
                <input
                  type="number"
                  min={1}
                  max={100000}
                  step={0.01}
                  value={alertDraftThreshold || ""}
                  onChange={(e) => setAlertDraftThreshold(e.target.value === "" ? 0 : Number(e.target.value))}
                  placeholder="输入金额"
                  className="h-9 w-32 rounded-lg border border-[#e5e5e5] px-3 text-sm tabular-nums focus:border-[#4d6bfe] focus:outline-none focus:ring-2 focus:ring-[#4d6bfe]/20"
                />
                <span className="text-xs text-[#a2a4a6]">CNY</span>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={saveAlert}
            disabled={savingAlert || (alertDraftEnabled && (!alertDraftThreshold || alertDraftThreshold <= 0))}
            className="mt-5 h-10 w-full rounded-lg bg-[#4d6bfe] text-sm font-medium text-white transition-colors hover:bg-[#3d57e0] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {savingAlert ? "保存中…" : "保存设置"}
          </button>
        </Modal>
      )}

      {/* timezone dialog */}
      {tzOpen && (
        <Modal
          title="选择时区"
          subtitle="用量图表与账单中的日期将按所选时区显示。"
          onClose={() => setTzOpen(false)}
        >
          <div className="mt-4 max-h-[300px] space-y-1 overflow-y-auto pr-1">
            {TIMEZONES.map((tz) => (
              <button
                key={tz.id}
                type="button"
                onClick={() => setTzDraftId(tz.id)}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${
                  tzDraftId === tz.id ? "bg-[#f0f2ff] font-medium text-[#4d6bfe]" : "text-[#212327] hover:bg-[#f5f5f5]"
                }`}
              >
                <span>{tz.label}</span>
                {tzDraftId === tz.id && <Check className="h-4 w-4" />}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={saveTimezone}
            disabled={savingTz}
            className="mt-5 h-10 w-full rounded-lg bg-[#4d6bfe] text-sm font-medium text-white transition-colors hover:bg-[#3d57e0] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {savingTz ? "保存中…" : "保存设置"}
          </button>
        </Modal>
      )}
    </main>
  );
}
